import { useState, useRef, useEffect, useLayoutEffect, forwardRef } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { clsx } from "clsx";

interface SearchableSelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  label?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
  variant?: "default" | "ghost";
}

export const SearchableSelect = forwardRef<
  HTMLSelectElement,
  SearchableSelectProps
>(
  (
    {
      label,
      error,
      options,
      placeholder,
      className,
      id,
      disabled,
      onChange,
      onBlur,
      value,
      defaultValue,
      name,
      variant = "default",
    },
    ref,
  ) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [internalValue, setInternalValue] = useState<string>(
      () => (value as string | undefined) ?? (defaultValue as string | undefined) ?? "",
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    // pozycja dropdownu (portal do body — nie obcina go overflow modala/scrolla)
    // `top` XOR `bottom`: below the trigger by default, above it when the viewport has no
    // room underneath (trigger low on the screen / inside a tall drawer) — otherwise the
    // portal renders past the bottom edge and the list looks like nothing opened at all.
    // `listMaxHeight` trims the scrollable list to the space actually available.
    const [coords, setCoords] = useState<{
      top?: number; bottom?: number; left: number; width: number; listMaxHeight: number;
    } | null>(null);

    const updateCoords = () => {
      const r = containerRef.current?.getBoundingClientRect();
      if (!r) return;
      // Long labels (contractor names) need room: never render the dropdown
      // narrower than 280px, but keep it pinned inside the viewport.
      const width = Math.min(Math.max(r.width, 280), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const LIST_MAX = 224; // = max-h-56
      const CHROME = 64; // search box + paddings
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const spaceAbove = r.top - 12;
      const fitsBelow = spaceBelow >= Math.min(LIST_MAX, 120) + CHROME;
      const above = !fitsBelow && spaceAbove > spaceBelow;
      const space = above ? spaceAbove : spaceBelow;
      const listMaxHeight = Math.max(80, Math.min(LIST_MAX, space - CHROME));
      setCoords(
        above
          ? { bottom: window.innerHeight - r.top + 4, left, width, listMaxHeight }
          : { top: r.bottom + 4, left, width, listMaxHeight },
      );
    };

    // Sync controlled value
    useEffect(() => {
      if (value !== undefined) setInternalValue(value as string);
    }, [value]);

    const selectedOption = options.find((o) => o.value === internalValue);

    const filtered = search.trim()
      ? options.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : options;

    const handleSelect = (optionValue: string) => {
      setInternalValue(optionValue);
      setSearch("");
      setOpen(false);
      if (onChange) {
        const fakeEvent = {
          target: { value: optionValue, name: name ?? "" },
          currentTarget: { value: optionValue, name: name ?? "" },
          type: "change",
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(fakeEvent);
      }
      // Restore focus to the trigger before the dropdown portal unmounts.
      // Otherwise focus falls back to <body> (the focused search input is
      // removed from the DOM), breaking native Tab order to the next field.
      triggerRef.current?.focus();
    };

    const handleTriggerClick = () => {
      if (disabled) return;
      const next = !open;
      if (next) updateCoords();
      setOpen(next);
      if (next) setTimeout(() => searchRef.current?.focus(), 30);
    };

    // Close on outside click (uwzględnia dropdown w portalu) + reposycjonowanie przy scrollu/resize
    useEffect(() => {
      if (!open) return;
      const handleMouseDown = (e: MouseEvent) => {
        const t = e.target as Node;
        if (!containerRef.current?.contains(t) && !dropdownRef.current?.contains(t)) {
          setOpen(false);
          setSearch("");
        }
      };
      const reposition = () => updateCoords();
      document.addEventListener("mousedown", handleMouseDown);
      window.addEventListener("scroll", reposition, true); // capture: łapie też scroll w modalu
      window.addEventListener("resize", reposition);
      return () => {
        document.removeEventListener("mousedown", handleMouseDown);
        window.removeEventListener("scroll", reposition, true);
        window.removeEventListener("resize", reposition);
      };
    }, [open]);

    // utrzymaj pozycję zsynchronizowaną po otwarciu
    useLayoutEffect(() => {
      if (open) updateCoords();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
      // "relative" anchors the sr-only native select below: without a positioned
      // ancestor its absolute box resolves against <html>, and when the field sits
      // below the fold Chrome extends the document's scrollable area past the app
      // shell (second page-level scrollbar; Firefox doesn't do this).
      <div className="relative flex flex-col gap-1">
        {label && (
          <label
            htmlFor={selectId}
            className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]"
          >
            {label}
          </label>
        )}

        {/* Hidden native select — keeps the ref for React Hook Form */}
        <select
          ref={ref}
          name={name}
          value={internalValue}
          onChange={() => {}}
          onBlur={onBlur}
          tabIndex={-1}
          className="sr-only"
          aria-hidden="true"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {/* Visible custom trigger + dropdown */}
        <div ref={containerRef} className="relative">
          <button
            ref={triggerRef}
            type="button"
            id={selectId}
            onClick={handleTriggerClick}
            disabled={disabled}
            className={clsx(
              "w-full text-left flex items-center gap-2",
              variant === "default" && [
                "input-base",
                !internalValue && "text-gray-400 dark:text-gray-500",
                error && "border-[var(--color-primary)]",
              ],
              variant === "ghost" && [
                "border-0 bg-transparent text-xs text-gray-500 dark:text-gray-400",
                "focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]",
                "rounded px-1 py-0.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700",
                !internalValue && "text-gray-400 dark:text-gray-500",
              ],
              disabled && "opacity-50 cursor-not-allowed",
              className,
            )}
          >
            <span className="flex-1 truncate min-w-0">
              {selectedOption?.label ?? placeholder ?? "—"}
            </span>
            <ChevronDown
              size={14}
              className={clsx(
                "shrink-0 text-gray-400 transition-transform duration-150",
                open && "rotate-180",
              )}
            />
          </button>

          {open && coords && createPortal(
            <div
              ref={dropdownRef}
              style={{
                position: "fixed", top: coords.top, bottom: coords.bottom,
                left: coords.left, width: coords.width, zIndex: 1000,
              }}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl"
            >
              <div className="p-2 border-b border-gray-100 dark:border-gray-700/50">
                <div className="relative">
                  <Search
                    size={12}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setOpen(false);
                        setSearch("");
                        triggerRef.current?.focus();
                      }
                      if (e.key === "Enter" && filtered[0]) {
                        e.preventDefault();
                        handleSelect(filtered[0].value);
                      }
                    }}
                    placeholder="Szukaj..."
                    className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
                  />
                </div>
              </div>

              <ul
                className="max-h-56 overflow-y-auto overflow-x-hidden py-1"
                style={{ maxHeight: coords.listMaxHeight }}
              >
                {placeholder && (
                  <li>
                    <button
                      type="button"
                      onClick={() => handleSelect("")}
                      className={clsx(
                        "w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50",
                        internalValue === "" &&
                          "bg-gray-50 dark:bg-gray-700/50",
                      )}
                    >
                      {placeholder}
                    </button>
                  </li>
                )}
                {filtered.length === 0 ? (
                  <li className="px-3 py-3 text-sm text-center text-gray-400">
                    Brak wyników
                  </li>
                ) : (
                  filtered.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        onClick={() => handleSelect(opt.value)}
                        className={clsx(
                          "w-full text-left px-3 py-2 text-sm break-words hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-900 dark:text-gray-100",
                          opt.value === internalValue &&
                            "bg-[var(--color-primary)]/10 font-medium text-[var(--color-primary)]",
                        )}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body,
          )}
        </div>

        {error && (
          <p className="text-xs font-mono text-[var(--color-primary)]">
            {error}
          </p>
        )}
      </div>
    );
  },
);
SearchableSelect.displayName = "SearchableSelect";
